/**
 * SumUp card terminals (#3498, feature 1321).
 *
 * Each workshop signs its own SumUp merchant agreement and receives its
 * terminals with it; OS Buddy only pairs them (PO decisions A1, A4). The
 * workshop stores its merchant code and an API key created in SumUp, tests
 * the connection and pairs terminals with the code shown on the terminal.
 * The API key is write-only; the API never returns it.
 *
 * Card payments go to a terminal only after a passed test and with at least
 * one paired terminal. Until then the till keeps the manual card reference.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { ApiError } from '@/lib/api';
import { describeApiError } from '@/lib/describeApiError';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteSumUpCredentials,
  usePairSumUpReader,
  useRemoveSumUpReader,
  useRenameSumUpReader,
  useSaveSumUpCredentials,
  useSumUpIntegration,
  useSumUpReaderStatus,
  useTestSumUpCredentials,
  type SumUpCredentialsStatus,
  type SumUpReader,
  type SumUpReaderStatus,
} from '@/hooks/useSumUpIntegration';

const MERCHANT_CODE_PATTERN = /^[A-Za-z0-9]{1,32}$/;
const PAIRING_CODE_PATTERN = /^[A-Za-z0-9]{8,9}$/;
const READER_NAME_MAX = 100;

const ERROR_KEYS: Record<string, string> = {
  SUMUP_ENCRYPTION_KEY_MISSING: 'sumup.errors.encryptionMissing',
  SUMUP_API_KEY_REQUIRED: 'sumup.errors.apiKeyRequired',
  SUMUP_CREDENTIALS_MISSING: 'sumup.errors.credentialsMissing',
  SUMUP_UNAUTHORIZED: 'sumup.errors.unauthorized',
  SUMUP_PAIRING_CODE_NOT_FOUND: 'sumup.errors.pairingCodeNotFound',
  SUMUP_READER_NOT_PENDING: 'sumup.errors.readerNotPending',
  SUMUP_REJECTED: 'sumup.errors.rejected',
  SUMUP_UNAVAILABLE: 'sumup.errors.unavailable',
  SUMUP_READER_NOT_FOUND: 'sumup.errors.readerNotFound',
};

const LAST_TEST_ERROR_KEYS: Record<string, string> = {
  SUMUP_TEST_UNAUTHORIZED: 'sumup.errors.testUnauthorized',
  SUMUP_TEST_REJECTED: 'sumup.errors.testRejected',
  SUMUP_TEST_HTTP: 'sumup.errors.testHttp',
  SUMUP_TEST_TIMEOUT: 'sumup.errors.testTimeout',
  SUMUP_TEST_NETWORK: 'sumup.errors.testNetwork',
  SUMUP_TEST_MALFORMED: 'sumup.errors.testMalformed',
  SUMUP_TEST_INVALID: 'sumup.errors.testInvalid',
  SUMUP_TEST_FAILED: 'sumup.errors.testFailed',
};

const ACTIVITY_KEYS: Record<SumUpReaderStatus['activity'], string> = {
  idle: 'sumup.activity.idle',
  selecting_tip: 'sumup.activity.selectingTip',
  waiting_for_card: 'sumup.activity.waitingForCard',
  waiting_for_pin: 'sumup.activity.waitingForPin',
  waiting_for_signature: 'sumup.activity.waitingForSignature',
  updating_firmware: 'sumup.activity.updatingFirmware',
  unknown: 'sumup.activity.unknown',
};

function lastTestErrorKey(error: string | undefined): string {
  return (error !== undefined ? LAST_TEST_ERROR_KEYS[error] : undefined) ?? 'sumup.errors.testFailed';
}

function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function statusChip(
  credentials: SumUpCredentialsStatus,
  enabled: boolean,
): { color: 'success' | 'warning' | 'error' | 'default'; labelKey: string } {
  if (enabled) return { color: 'success', labelKey: 'sumup.statusReady' };
  if (credentials.lastTest?.success === false) return { color: 'error', labelKey: 'sumup.statusFailed' };
  if (credentials.credentialsReady) return { color: 'warning', labelKey: 'sumup.statusNoReaders' };
  if (credentials.hasApiKey) return { color: 'warning', labelKey: 'sumup.statusUntested' };
  return { color: 'default', labelKey: 'sumup.statusNotConfigured' };
}

function ReaderStatusChip({ readerId, enabled }: { readerId: string; enabled: boolean }) {
  const { t } = useTranslation('settings');
  const status = useSumUpReaderStatus(readerId, enabled);
  if (!enabled) return null;
  if (status.isLoading) return <CircularProgress size={16} aria-label={t('sumup.readerStatusLoading')} />;
  if (status.isError || !status.data) {
    return <Chip size="small" variant="outlined" label={t('sumup.readerStatusUnknown')} />;
  }
  const { connection, activity, batteryLevel } = status.data;
  const label =
    connection === 'online'
      ? t('sumup.readerOnline', { activity: t(ACTIVITY_KEYS[activity]) })
      : t('sumup.readerOffline');
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip size="small" color={connection === 'online' ? 'success' : 'default'} label={label} />
      {batteryLevel !== null && (
        <Typography variant="caption" color="text.secondary">
          {t('sumup.readerBattery', { level: batteryLevel })}
        </Typography>
      )}
    </Stack>
  );
}

export function SumUpSettingsTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const query = useSumUpIntegration(true);
  const saveCredentials = useSaveSumUpCredentials();
  const testCredentials = useTestSumUpCredentials();
  const deleteCredentials = useDeleteSumUpCredentials();
  const pairReader = usePairSumUpReader();
  const renameReader = useRenameSumUpReader();
  const removeReader = useRemoveSumUpReader();

  const [merchantCode, setMerchantCode] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pairingCode, setPairingCode] = useState('');
  const [readerName, setReaderName] = useState('');
  const [renaming, setRenaming] = useState<SumUpReader | null>(null);
  const [newName, setNewName] = useState('');
  const [removing, setRemoving] = useState<SumUpReader | null>(null);

  useEffect(() => {
    const credentials = query.data?.credentials;
    if (!credentials) return;
    setMerchantCode(credentials.merchantCode ?? '');
    setApiKey('');
  }, [query.data?.credentials]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={tc('loading')} />
      </Box>
    );
  }
  if (query.isError || !query.data) {
    return <Alert severity="error">{t('sumup.loadFailed')}</Alert>;
  }

  const { affiliateConfigured, credentials, readers, enabled } = query.data;
  const merchantTrimmed = merchantCode.trim();
  const merchantInvalid = merchantTrimmed !== '' && !MERCHANT_CODE_PATTERN.test(merchantTrimmed);
  const merchantChanging = credentials.merchantCode !== null && merchantTrimmed !== credentials.merchantCode;

  const showError = (error: unknown, fallbackKey: string) => {
    const known = ERROR_KEYS[errorCode(error) ?? ''];
    notify.error(tc('error'), known ? t(known) : describeApiError(error, tc, { fallback: t(fallbackKey) }));
  };

  const canSave =
    credentials.encryptionAvailable &&
    merchantTrimmed !== '' &&
    !merchantInvalid &&
    (apiKey.trim() !== '' || credentials.hasApiKey) &&
    !saveCredentials.isPending;

  const handleSave = async () => {
    if (!canSave) return;
    let saved: SumUpCredentialsStatus;
    try {
      saved = await saveCredentials.mutateAsync({
        merchantCode: merchantTrimmed,
        ...(apiKey.trim() !== '' ? { apiKey: apiKey.trim() } : {}),
      });
      setApiKey('');
      notify.success(t('sumup.saved'), t('sumup.savedDesc'));
    } catch (error) {
      showError(error, 'sumup.errors.saveFailed');
      return;
    }
    // A changed credential clears the last test, and the till offers no
    // terminal until one passes — test right away instead (#3848).
    if (saved.hasApiKey && saved.merchantCode !== null && saved.lastTest === null) await runTest();
  };

  const canTest =
    credentials.encryptionAvailable &&
    credentials.hasApiKey &&
    credentials.merchantCode !== null &&
    !testCredentials.isPending &&
    !saveCredentials.isPending;

  const runTest = async () => {
    try {
      const result = await testCredentials.mutateAsync(undefined);
      if (result.success) {
        notify.success(t('sumup.testSuccess'), t('sumup.testSuccessDesc'));
      } else {
        notify.error(tc('error'), t(lastTestErrorKey(result.credentials.lastTest?.error)));
      }
    } catch (error) {
      showError(error, 'sumup.errors.testFailed');
    }
  };

  const handleTest = async () => {
    if (canTest) await runTest();
  };

  const canDelete = (credentials.hasApiKey || credentials.merchantCode !== null) && !deleteCredentials.isPending;

  const handleDelete = async () => {
    try {
      await deleteCredentials.mutateAsync(undefined);
      setConfirmDelete(false);
      setMerchantCode('');
      setApiKey('');
      notify.success(t('sumup.deleted'), t('sumup.deletedDesc'));
    } catch (error) {
      showError(error, 'sumup.errors.deleteFailed');
    }
  };

  const pairingTrimmed = pairingCode.trim();
  const pairingInvalid = pairingTrimmed !== '' && !PAIRING_CODE_PATTERN.test(pairingTrimmed);
  const canPair =
    credentials.hasApiKey &&
    credentials.merchantCode !== null &&
    PAIRING_CODE_PATTERN.test(pairingTrimmed) &&
    readerName.trim() !== '' &&
    !pairReader.isPending;

  const handlePair = async () => {
    if (!canPair) return;
    try {
      const result = await pairReader.mutateAsync({ pairingCode: pairingTrimmed, name: readerName.trim() });
      setPairingCode('');
      setReaderName('');
      notify.success(t('sumup.paired'), t('sumup.pairedDesc', { name: result.reader.name }));
    } catch (error) {
      showError(error, 'sumup.errors.pairFailed');
    }
  };

  const handleRename = async () => {
    if (renaming === null || newName.trim() === '') return;
    try {
      await renameReader.mutateAsync({ id: renaming.id, name: newName.trim() });
      setRenaming(null);
      notify.success(t('sumup.renamed'), t('sumup.renamedDesc'));
    } catch (error) {
      showError(error, 'sumup.errors.renameFailed');
    }
  };

  const handleRemove = async () => {
    if (removing === null) return;
    try {
      await removeReader.mutateAsync(removing.id);
      setRemoving(null);
      notify.success(t('sumup.removed'), t('sumup.removedDesc'));
    } catch (error) {
      showError(error, 'sumup.errors.removeFailed');
    }
  };

  const chip = statusChip(credentials, enabled);
  const lastTest = credentials.lastTest;

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="h6">{t('sumup.title')}</Typography>
          <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('sumup.intro')}
        </Typography>
      </Box>

      {!affiliateConfigured && <Alert severity="info">{t('sumup.affiliateMissing')}</Alert>}
      {!credentials.encryptionAvailable && <Alert severity="warning">{t('sumup.encryptionMissing')}</Alert>}
      {enabled && <Alert severity="success">{t('sumup.enabledHint')}</Alert>}
      {lastTest?.success === false && <Alert severity="error">{t(lastTestErrorKey(lastTest.error))}</Alert>}
      {readers.length > 0 && !credentials.credentialsReady && (
        <Alert severity="warning">{t('sumup.readersHiddenUntilTested')}</Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label={t('sumup.merchantCode')}
          value={merchantCode}
          onChange={(e) => setMerchantCode(e.target.value)}
          error={merchantInvalid}
          helperText={
            merchantInvalid
              ? t('sumup.merchantCodeInvalid')
              : merchantChanging && readers.length > 0
                ? t('sumup.merchantCodeChangeWarning')
                : t('sumup.merchantCodeHint')
          }
          autoComplete="off"
          required
          fullWidth
        />
        <TextField
          label={t('sumup.apiKey')}
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          helperText={credentials.hasApiKey ? t('sumup.apiKeySetHint') : t('sumup.apiKeyHint')}
          autoComplete="new-password"
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={saveCredentials.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {t('sumup.save')}
          </Button>
          <Button
            variant="outlined"
            startIcon={testCredentials.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => void handleTest()}
            disabled={!canTest}
          >
            {t('sumup.test')}
          </Button>
          <Button color="error" onClick={() => setConfirmDelete(true)} disabled={!canDelete}>
            {t('sumup.delete')}
          </Button>
        </Stack>
      </Stack>

      {credentials.hasApiKey && credentials.merchantCode !== null && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1">{t('sumup.readersTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('sumup.readersIntro')}
            </Typography>
          </Box>

          {readers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('sumup.noReaders')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {readers.map((reader) => (
                <ListItem
                  key={reader.id}
                  divider
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('sumup.rename')}>
                        <IconButton
                          aria-label={t('sumup.renameReader', { name: reader.name })}
                          onClick={() => {
                            setRenaming(reader);
                            setNewName(reader.name);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('sumup.remove')}>
                        <IconButton
                          aria-label={t('sumup.removeReader', { name: reader.name })}
                          onClick={() => setRemoving(reader)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={reader.name}
                    secondary={
                      <Stack component="span" spacing={0.5} sx={{ mt: 0.5 }}>
                        {reader.serialNumber !== null && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {t('sumup.readerSerial', { serial: reader.serialNumber })}
                          </Typography>
                        )}
                        <ReaderStatusChip readerId={reader.id} enabled={credentials.credentialsReady} />
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label={t('sumup.pairingCode')}
              value={pairingCode}
              onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
              error={pairingInvalid}
              helperText={pairingInvalid ? t('sumup.pairingCodeInvalid') : t('sumup.pairingCodeHint')}
              autoComplete="off"
              inputProps={{ maxLength: 9 }}
            />
            <TextField
              label={t('sumup.readerName')}
              value={readerName}
              onChange={(e) => setReaderName(e.target.value)}
              helperText={t('sumup.readerNameHint')}
              inputProps={{ maxLength: READER_NAME_MAX }}
            />
            <Button
              variant="outlined"
              startIcon={pairReader.isPending ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
              onClick={() => void handlePair()}
              disabled={!canPair}
              sx={{ mt: { sm: 1 } }}
            >
              {t('sumup.pair')}
            </Button>
          </Stack>
        </Stack>
      )}

      <ResponsiveDialog
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        mobileTitle={t('sumup.renameTitle')}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('sumup.renameTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('sumup.readerName')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            inputProps={{ maxLength: READER_NAME_MAX }}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>{tc('cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleRename()}
            disabled={newName.trim() === '' || renameReader.isPending}
          >
            {tc('save')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ConfirmDialog
        open={removing !== null}
        title={t('sumup.removeTitle')}
        message={t('sumup.removeMessage', { name: removing?.name ?? '' })}
        confirmColor="error"
        confirmText={t('sumup.remove')}
        onConfirm={() => void handleRemove()}
        onCancel={() => setRemoving(null)}
        loading={removeReader.isPending}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('sumup.deleteTitle')}
        message={t('sumup.deleteMessage')}
        confirmColor="error"
        confirmText={tc('delete')}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
        loading={deleteCredentials.isPending}
      />
    </Stack>
  );
}
